import { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  House, UsersThree, Funnel, EnvelopeSimple, Star, ChatCircleDots,
  Phone, ChartLineUp, Gear, SignOut, Buildings, Lightning, ClipboardText,
  BellRinging, ListChecks, Tray, CaretUpDown, Check, Sparkle, Bell, UserGear, Wrench,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { NotificationBell } from "@/components/NotificationBell";

const copilotNav = [
  { to: "/app", label: "Dashboard", icon: House, end: true, id: "dashboard" },
  { to: "/app/guests", label: "Guests", icon: UsersThree, id: "guests" },
  { to: "/app/segments", label: "Segments", icon: Funnel, id: "segments" },
  { to: "/app/email", label: "Email", icon: EnvelopeSimple, id: "email" },
  { to: "/app/automations", label: "Automations", icon: Lightning, id: "automations" },
  { to: "/app/reputation", label: "Reputation", icon: Star, id: "reputation" },
  { to: "/app/surveys", label: "Surveys", icon: ClipboardText, id: "surveys" },
  { to: "/app/chat", label: "Chat", icon: ChatCircleDots, id: "chat" },
  { to: "/app/voice", label: "Voice", icon: Phone, id: "voice" },
  { to: "/app/analytics", label: "Analytics", icon: ChartLineUp, id: "analytics" },
  { to: "/app/alerts", label: "Alerts", icon: BellRinging, id: "alerts" },
  { to: "/app/audit", label: "Audit Log", icon: ListChecks, id: "audit" },
];

const soysosNav = [
  { to: "/app/soysos", label: "Requests", icon: Tray, end: true, id: "sos-tickets" },
  { to: "/app/soysos/reports", label: "Reports", icon: ChartLineUp, id: "sos-reports" },
  { to: "/app/soysos/workers", label: "Workers", icon: Wrench, id: "sos-workers" },
  { to: "/app/soysos/stays", label: "In-House Guests", icon: Bell, id: "sos-stays" },
];

const PLATFORMS = {
  copilot360: { label: "Copilot360", sub: "Guest CRM & CDP", icon: Sparkle, home: "/app", color: "#EA580C" },
  soysos: { label: "SoySOS", sub: "Guest Requests", icon: Bell, home: "/app/soysos", color: "#0EA5E9" },
};

function PlatformSwitcher({ current, allowed, onSwitch }) {
  const [open, setOpen] = useState(false);
  const cur = PLATFORMS[current];
  const Icon = cur.icon;
  return (
    <div className="relative">
      <button
        data-testid="platform-switcher"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2.5 rounded-md px-2.5 py-2 bg-background border border-border hover:border-primary/40 transition-colors"
      >
        <div className="h-7 w-7 rounded flex items-center justify-center text-white shrink-0" style={{ background: cur.color }}>
          <Icon size={16} weight="fill" />
        </div>
        <div className="min-w-0 flex-1 text-left">
          <div className="text-[13px] font-semibold leading-none truncate">{cur.label}</div>
          <div className="text-[10.5px] text-muted-foreground mt-0.5 truncate">{cur.sub}</div>
        </div>
        <CaretUpDown size={15} className="text-muted-foreground shrink-0" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 right-0 mt-1 z-50 rounded-md border border-border bg-card shadow-lg p-1" data-testid="platform-menu">
            {allowed.map((key) => {
              const p = PLATFORMS[key];
              const PIcon = p.icon;
              return (
                <button
                  key={key}
                  data-testid={`platform-option-${key}`}
                  onClick={() => { setOpen(false); onSwitch(key); }}
                  className="w-full flex items-center gap-2.5 rounded px-2 py-2 hover:bg-secondary transition-colors"
                >
                  <div className="h-6 w-6 rounded flex items-center justify-center text-white shrink-0" style={{ background: p.color }}>
                    <PIcon size={13} weight="fill" />
                  </div>
                  <div className="min-w-0 flex-1 text-left">
                    <div className="text-[12.5px] font-medium leading-none truncate">{p.label}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5 truncate">{p.sub}</div>
                  </div>
                  {current === key && <Check size={14} className="text-primary" />}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export function Sidebar() {
  const { property, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const allowed = (user?.platforms && user.platforms.length ? user.platforms : ["copilot360", "soysos"])
    .filter((p) => PLATFORMS[p]);
  const platform = location.pathname.startsWith("/app/soysos") ? "soysos" : "copilot360";
  const nav = platform === "soysos" ? soysosNav : copilotNav;
  const accent = PLATFORMS[platform].color;

  return (
    <aside className="w-[248px] shrink-0 border-r border-border bg-secondary/40 flex flex-col h-screen sticky top-0">
      <div className="px-3 py-3 border-b border-border">
        {allowed.length > 1 ? (
          <PlatformSwitcher current={platform} allowed={allowed} onSwitch={(k) => navigate(PLATFORMS[k].home)} />
        ) : (
          <div className="flex items-center gap-2.5 px-1 py-1">
            <div className="h-8 w-8 rounded-md flex items-center justify-center text-white font-heading font-bold text-lg" style={{ background: accent }}>{PLATFORMS[platform].label[0]}</div>
            <div><div className="font-heading font-semibold text-[15px] leading-none">{PLATFORMS[platform].label}</div><div className="text-[11px] text-muted-foreground mt-1">{PLATFORMS[platform].sub}</div></div>
          </div>
        )}
      </div>

      <div className="px-3 py-3 border-b border-border">
        <div className="flex items-center gap-2.5 rounded-md px-2.5 py-2 bg-background border border-border">
          <div className="h-7 w-7 rounded bg-primary/10 flex items-center justify-center text-primary">
            <Buildings size={16} weight="fill" />
          </div>
          <div className="min-w-0">
            <div className="text-[13px] font-medium truncate" data-testid="sidebar-property-name">{property?.name || "Loading…"}</div>
            <div className="text-[11px] text-success flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-success inline-block" /> PMS connected
            </div>
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5">
        {nav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={`sidebar-nav-${n.id}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-md px-3 py-2 text-[13.5px] font-medium transition-colors duration-150 ${
                isActive ? "text-white shadow-sm" : "text-muted-foreground hover:bg-background hover:text-foreground"
              }`
            }
            style={({ isActive }) => (isActive ? { background: accent } : {})}
          >
            {({ isActive }) => (<><n.icon size={18} weight={isActive ? "fill" : "regular"} />{n.label}</>)}
          </NavLink>
        ))}
      </nav>

      <div className="px-3 py-3 border-t border-border space-y-1.5">
        <NotificationBell />
        {user?.is_admin && (
          <NavLink to="/app/users" data-testid="sidebar-nav-users"
            className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2 text-[13.5px] font-medium transition-colors ${isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-background hover:text-foreground"}`}>
            <UserGear size={18} /> Users
          </NavLink>
        )}
        <NavLink
          to="/app/settings"
          data-testid="sidebar-nav-settings"
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-md px-3 py-2 text-[13.5px] font-medium transition-colors duration-150 ${
              isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-background hover:text-foreground"
            }`
          }
        >
          <Gear size={18} /> Settings
        </NavLink>
        <div className="flex items-center gap-2.5 px-2.5 pt-2">
          <img src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=80&h=80&fit=crop&q=80" alt="" className="h-7 w-7 rounded-full object-cover" />
          <div className="min-w-0 flex-1">
            <div className="text-[12.5px] font-medium truncate">{user?.name}</div>
            <div className="text-[11px] text-muted-foreground truncate">{user?.email}</div>
          </div>
          <button
            data-testid="logout-button"
            onClick={async () => { await logout(); navigate("/login"); }}
            className="text-muted-foreground hover:text-destructive transition-colors"
            title="Sign out"
          >
            <SignOut size={17} />
          </button>
        </div>
      </div>
    </aside>
  );
}
